/**
 * Vocabulário do firewall de IP do host — puro, sem React, para o check de contrato.
 *
 * Duas coisas que a tela não pode errar:
 *  - o desfecho de um comando de IP só é AFIRMADO em `executed`/`skipped`
 *    (confirmado pelo agente); `delivered` é "escrito na stream", nada mais;
 *  - a validação do cliente é conveniência. O servidor é a autoridade, e a
 *    mesma regra roda de novo no agente.
 */
import type { Tone } from '../../components/primitives/Metric.tsx'
import type { AgentCommand } from '../api/admin.ts'
import type { AlertResponseAction } from '../sse/types.ts'

/* ── validação (conveniência) ────────────────────────────────────────────── */

const IPV4 = /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/

function isHexGroup(part: string): boolean {
  return /^[0-9a-fA-F]{1,4}$/.test(part)
}

/** IPv4 estrito: quatro octetos sem zero à esquerda (`010.0.0.1` não vale). */
export function isIpv4(text: string): boolean {
  return IPV4.test(text)
}

/** IPv6 estrito: sem prefixo e sem zona (`%eth0`). Aceita `::` e o final IPv4 embutido. */
export function isIpv6(text: string): boolean {
  if (!text.includes(':') || text.includes('%') || text.includes('/')) return false
  const halves = text.split('::')
  if (halves.length > 2) return false

  const groupsOf = (half: string): string[] | null => (half === '' ? [] : half.split(':'))
  const head = groupsOf(halves[0]!)
  const tail = halves.length === 2 ? groupsOf(halves[1]!) : []
  if (head === null || tail === null) return false

  const all = [...head, ...tail]
  let count = 0
  for (let i = 0; i < all.length; i += 1) {
    const part = all[i]!
    if (i === all.length - 1 && part.includes('.')) {
      // IPv4 embutido só no último grupo, e conta por dois.
      if (!isIpv4(part)) return false
      count += 2
    } else {
      if (!isHexGroup(part)) return false
      count += 1
    }
  }
  return halves.length === 2 ? count < 8 : count === 8
}

export type IpFamily = 4 | 6

export function ipFamily(text: string): IpFamily | null {
  if (isIpv4(text)) return 4
  if (isIpv6(text)) return 6
  return null
}

/** Mais estreita que isto o servidor recusa: `agent_command_cidr_too_wide`. */
export const MIN_PREFIX_V4 = 8
export const MIN_PREFIX_V6 = 32

export type CidrProblem = 'invalid' | 'too_wide'

export interface ParsedCidr {
  ip: string
  family: IpFamily
  prefix: number
}

/** `ip` ou `ip/prefixo`. IP sozinho vale /32 (IPv4) ou /128 (IPv6). */
export function parseCidr(text: string): ParsedCidr | null {
  const [ip, rawPrefix, ...rest] = text.split('/')
  if (ip === undefined || rest.length > 0) return null
  const family = ipFamily(ip)
  if (family === null) return null
  const max = family === 4 ? 32 : 128
  if (rawPrefix === undefined) return { ip, family, prefix: max }
  if (!/^\d{1,3}$/.test(rawPrefix)) return null
  const prefix = Number(rawPrefix)
  return prefix > max ? null : { ip, family, prefix }
}

/** Texto do campo "IP a bloquear" → o motivo de recusa, ou `null` se parece válido. */
export function ipProblem(text: string): string | null {
  const value = text.trim()
  if (value === '') return null
  return ipFamily(value) === null ? 'Não é um IPv4 ou IPv6 válido (sem prefixo, sem zona).' : null
}

/** Texto do campo "IP ou rede a permitir" → o motivo de recusa, ou `null`. */
export function cidrProblem(text: string): string | null {
  const value = text.trim()
  if (value === '') return null
  const parsed = parseCidr(value)
  if (parsed === null) return 'Não é um IP nem uma rede (CIDR) válida.'
  const min = parsed.family === 4 ? MIN_PREFIX_V4 : MIN_PREFIX_V6
  return parsed.prefix < min
    ? `Rede larga demais: o mínimo é /${MIN_PREFIX_V4} no IPv4 e /${MIN_PREFIX_V6} no IPv6.`
    : null
}

/* ── comandos de IP ──────────────────────────────────────────────────────── */

export const IP_COMMAND_KINDS = ['block_ip', 'unblock_ip', 'allow_ip', 'revoke_allow_ip'] as const

export function isIpCommandKind(kind: string): boolean {
  return (IP_COMMAND_KINDS as readonly string[]).includes(kind)
}

/** `challenge_ip` (Web Defense): também tem um IP como alvo e também aparece "a caminho". */
export function isIpTargetKind(kind: string): boolean {
  return isIpCommandKind(kind) || kind === 'challenge_ip'
}

const KIND_LABEL: Record<string, string> = {
  block_ip: 'Bloquear IP',
  unblock_ip: 'Desbloquear IP',
  allow_ip: 'Permitir IP/rede',
  revoke_allow_ip: 'Retirar permissão',
  challenge_ip: 'Pôr em challenge',
}

export function ipCommandLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind
}

/**
 * O alvo canônico de um comando de IP: `params` é JSON em string
 * (`'{"ip":"…"}'` ou `'{"cidr":"…"}'`). `null` quando ausente ou malformado.
 */
export function commandTarget(command: { params?: string | null }): string | null {
  if (typeof command.params !== 'string') return null
  try {
    const parsed: unknown = JSON.parse(command.params)
    if (typeof parsed !== 'object' || parsed === null) return null
    const record = parsed as Record<string, unknown>
    const value = record['ip'] ?? record['cidr']
    return typeof value === 'string' && value !== '' ? value : null
  } catch {
    return null
  }
}

/** Os comandos de IP ainda abertos (na fila ou entregues), do que veio em `GET …/commands?openOnly=true`. */
export function openIpCommands(items: readonly AgentCommand[]): AgentCommand[] {
  return items.filter(
    (c) => isIpTargetKind(c.kind) && (c.state === 'pending' || c.state === 'delivered'),
  )
}

export interface IpOutcome {
  tone: Tone
  headline: string
  /** O agente confirmou (`executed`/`skipped`): hora de reler as listas. */
  confirmed: boolean
}

/**
 * O que a tela pode AFIRMAR depois de um comando de IP. Como no desbloqueio de
 * host, `delivered` nunca vira "feito": só a confirmação do agente vale.
 */
export function ipOutcome(kind: string, state: string, target: string | null): IpOutcome {
  const who = target ?? 'o alvo'
  const confirmedText: Record<string, { executed: string; skipped: string }> = {
    block_ip: {
      executed: `IP ${who} bloqueado: o agente confirmou.`,
      skipped: `O agente confirmou que ${who} já estava bloqueado.`,
    },
    unblock_ip: {
      executed: `IP ${who} desbloqueado: o agente confirmou.`,
      skipped: `${who} já não estava bloqueado.`,
    },
    allow_ip: {
      executed: `${who} entrou na allowlist: o agente confirmou.`,
      skipped: `${who} já estava na allowlist.`,
    },
    revoke_allow_ip: {
      executed: `${who} saiu da allowlist: o agente confirmou.`,
      skipped: `${who} já não estava na allowlist.`,
    },
    challenge_ip: {
      executed: `${who} em challenge: o agente confirmou.`,
      skipped: `${who} já tinha um challenge aberto nesta máquina.`,
    },
  }
  const label = ipCommandLabel(kind).toLowerCase()
  switch (state) {
    case 'pending':
      return { tone: 'accent', headline: 'Na fila — aguardando o agente buscar o comando.', confirmed: false }
    case 'delivered':
      return {
        tone: 'warn',
        headline: 'Entregue ao agente, sem confirmação.',
        confirmed: false,
      }
    case 'executed':
    case 'skipped': {
      const text = confirmedText[kind]?.[state]
      return {
        tone: 'ok',
        headline: text ?? `Comando confirmado pelo agente (${state}).`,
        confirmed: true,
      }
    }
    case 'failed':
      return { tone: 'crit', headline: `O comando (${label}) FALHOU. Nada mudou no firewall.`, confirmed: false }
    case 'expired':
      return { tone: 'warn', headline: 'O prazo venceu sem confirmação. Nada mudou no firewall.', confirmed: false }
    case 'cancelled':
      return { tone: 'neutral', headline: 'Comando cancelado. Nada mudou no firewall.', confirmed: false }
    default:
      return {
        tone: 'neutral',
        headline: `Estado "${state}" desconhecido.`,
        confirmed: false,
      }
  }
}

/** Origem de um bloqueio: o agente por regra, ou um operador. */
export function originLabel(origin: string): string {
  return origin === 'automatic' ? 'automático' : origin === 'operator' ? 'operador' : origin
}

/* ── alertas: ação block_ip ──────────────────────────────────────────────── */

/**
 * Frase de uma ação `block_ip` do alerta: "IP 203.0.113.7 bloqueado". `null`
 * para qualquer outra ação ou quando o alvo não trouxe IP. O desfecho decide o
 * verbo — `simulated` nunca diz "bloqueado".
 */
export function blockIpSummary(item: AlertResponseAction): string | null {
  const ip = item.target?.ip
  if (item.action !== 'block_ip' || ip == null || ip === '') return null
  switch (item.status) {
    case 'executed':
      return `IP ${ip} bloqueado`
    case 'skipped':
      return `IP ${ip} já estava bloqueado`
    case 'simulated':
      return `IP ${ip} seria bloqueado (simulação — nada foi tocado)`
    case 'failed':
      return `Falha ao bloquear o IP ${ip}`
    default:
      return `IP ${ip} · ${item.status ?? 'desfecho não informado'}`
  }
}

/** Regras de varredura de portas do Argos (TA0043/T1595); `data.key` = IP de quem varre. */
export const PORT_SCAN_RULES: readonly string[] = [
  'argos.win.reconhecimento.varredura_de_portas',
  'argos.lin.reconhecimento.varredura_de_portas',
]

/**
 * O IP de quem varreu, para os atalhos do alerta: `data.key` quando é IP, senão o
 * `target.ip` do `block_ip`. `null` quando o alerta não é de varredura ou nenhum
 * dos dois é um IP — nunca se oferece atalho sobre texto arbitrário.
 */
export function scannerIpOf(alert: {
  rule_id: string | null
  response: readonly AlertResponseAction[]
  key?: string | null
}): string | null {
  const blocked = alert.response.find((r) => r.action === 'block_ip' && r.target?.ip != null)
  const isScan = (alert.rule_id !== null && PORT_SCAN_RULES.includes(alert.rule_id)) || blocked !== undefined
  if (!isScan) return null
  const candidates = [alert.key, blocked?.target?.ip]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && ipFamily(candidate.trim()) !== null) return candidate.trim()
  }
  return null
}
